import type { EvidenciaConectividad } from '@ausentia/shared';

/**
 * Estado de red SIMULADO para demostrar el modo contingencia en el navegador.
 * Un toggle pone la app "sin conexión": los envíos van al outbox y se captura
 * automáticamente la evidencia de la falta de conectividad (intentos fallidos
 * con timestamps), que es lo que el Dec. 407/2026 exige acreditar (decisión
 * 2.4). En la app nativa esto lo provee el sistema operativo (estado de radio,
 * NetworkCallback); acá lo emulamos de forma fiel.
 */

const CLAVE = 'ausentia.red.online';
type Oyente = (online: boolean) => void;
const oyentes = new Set<Oyente>();
const intentosFallidos: { en: string; error: string }[] = [];

export function estaOnline(): boolean {
  const guardado = localStorage.getItem(CLAVE);
  if (guardado !== null) return guardado === '1';
  return navigator.onLine;
}

export function fijarOnline(online: boolean) {
  localStorage.setItem(CLAVE, online ? '1' : '0');
  if (online) intentosFallidos.length = 0; // al recuperar red, se cierra la evidencia
  for (const o of oyentes) o(online);
}

export function alternarRed(): boolean {
  const nuevo = !estaOnline();
  fijarOnline(nuevo);
  return nuevo;
}

export function suscribirRed(o: Oyente): () => void {
  oyentes.add(o);
  return () => oyentes.delete(o);
}

/** Registra un intento fallido de envío mientras no hay conexión. */
export function registrarIntentoFallido(error = 'NetworkUnreachable') {
  intentosFallidos.push({ en: new Date().toISOString(), error });
  if (intentosFallidos.length > 50) intentosFallidos.shift();
}

export function evidenciaContingencia(): EvidenciaConectividad {
  return {
    estadoRed: 'SIN_SERVICIO',
    intentosFallidos: [...intentosFallidos],
  };
}

export function descripcionDispositivo(): string {
  return `Navegador demo · ${navigator.userAgent.slice(0, 80)}`;
}

/** Geolocalización; si el navegador no la concede, valor demo de San Luis. */
export async function capturarGeo(): Promise<{
  lat: number;
  lng: number;
  precisionMetros: number;
  proveedor: string;
  esSimulada: boolean;
  capturadaEn: string;
}> {
  const fallback = {
    lat: -33.295,
    lng: -66.3356,
    precisionMetros: 12,
    proveedor: 'demo',
    esSimulada: false,
    capturadaEn: new Date().toISOString(),
  };
  if (!('geolocation' in navigator)) return fallback;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          precisionMetros: pos.coords.accuracy ?? 20,
          proveedor: 'navegador',
          esSimulada: false,
          capturadaEn: new Date().toISOString(),
        }),
      () => resolve(fallback),
      { enableHighAccuracy: true, timeout: 4000 },
    );
  });
}
